import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { recordConsent } from "@/lib/policyConsent";

const STORAGE_KEY = "wekit-cookie-consent";
const OPEN_EVENT = "wekit-cookie-open";

/** Reopen the banner from anywhere (footer, policy pages). */
export function openCookiePreferences() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/** Current choice: "accepted" | "rejected" | null (not chosen yet). */
export function cookieChoice(): "accepted" | "rejected" | null {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return v?.value ?? null;
  } catch {
    return null;
  }
}

const link = "text-primary underline underline-offset-2";
const btn =
  "min-h-11 px-4 text-left text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

/** Compact IBM-style bottom consent bar with checkboxes; every choice is recorded. */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [analytics, setAnalytics] = useState(() => cookieChoice() === "accepted");

  useEffect(() => {
    let t: number | undefined;
    if (!cookieChoice()) t = window.setTimeout(() => setVisible(true), 800);
    const open = () => setVisible(true);
    window.addEventListener(OPEN_EVENT, open);
    return () => {
      if (t) clearTimeout(t);
      window.removeEventListener(OPEN_EVENT, open);
    };
  }, []);

  const persist = (allowAnalytics: boolean, choice: "accepted" | "rejected" | "custom") => {
    const value = allowAnalytics ? "accepted" : "rejected";
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ value, at: new Date().toISOString(), version: 1 }));
    } catch {
      /* storage unavailable */
    }
    // Opting out drops anything already buffered so nothing more is sent.
    window.dispatchEvent(new Event(allowAnalytics ? "wekit-analytics-on" : "wekit-analytics-off"));
    void recordConsent({ kind: "cookies", policy: "cookies", choice, analytics: allowAnalytics });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="About cookies on this site"
      className="theme-reading print:hidden fixed inset-x-0 bottom-0 z-[120] border-t border-border bg-background text-foreground shadow-[0_-8px_30px_hsl(0_0%_0%/0.18)] animate-in slide-in-from-bottom-4 fade-in duration-300 motion-reduce:animate-none"
    >
      <div className="mx-auto grid max-w-7xl gap-3 px-4 py-3 text-sm leading-5 sm:px-6 md:grid-cols-[1.3fr_1fr_auto] md:gap-6">
        <div>
          <h2 className="font-semibold">About cookies on this site</h2>
          <p className="text-foreground/80">
            Required cookies keep the site working. With your consent, analytics cookies help us improve it. Read our{" "}
            <Link to="/cookies" className={link}>Cookie Policy</Link> and{" "}
            <Link to="/privacy" className={link}>Privacy Policy</Link>.
          </p>
        </div>
        <fieldset className="space-y-1">
          <legend className="sr-only">Cookie choices</legend>
          <label className="flex min-h-8 items-center gap-2 text-foreground/70">
            <input type="checkbox" checked disabled className="h-4 w-4 accent-primary" /> Required (always on)
          </label>
          <label className="flex min-h-8 cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />{" "}
            Analytics and improvement
          </label>
          <p className="text-xs text-foreground/60">
            Also see <Link to="/terms" className={link}>Terms</Link>, <Link to="/child-safety" className={link}>Child Safety</Link>,{" "}
            <Link to="/responsible-ai" className={link}>Responsible AI</Link>.
          </p>
        </fieldset>
        <div className="grid grid-cols-3 gap-2 md:w-52 md:grid-cols-1">
          <button type="button" onClick={() => persist(true, "accepted")} className={`${btn} bg-primary text-primary-foreground hover:bg-primary/90`}>
            Accept all
          </button>
          <button type="button" onClick={() => persist(analytics, "custom")} className={`${btn} border border-primary text-primary hover:bg-primary/10`}>
            Save choices
          </button>
          <button type="button" onClick={() => persist(false, "rejected")} className={`${btn} border border-border hover:bg-muted`}>
            Required only
          </button>
        </div>
      </div>
    </div>
  );
}
